import * as React from 'react';
import classnames from 'classnames';

import BarChartProgress from 'components/graphs/bar-chart-progress/BarChartProgress';

import {
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_INSTAGRAM_STORY
} from 'utils/deliverables';
import { getNumFollowers } from 'utils/influencers';
import { __t, applyTranslations } from 'utils/language';
import { COLORS } from 'utils/reporting';
import { getMetricTypeWidth } from 'modules/reporting/utils/reporting-task-types';

import './creator-follower-progress-widget.scss';

applyTranslations(require('./creator-follower-progress-widget.i18n.json'));

import type { BarChartProgressItem } from 'types';
import type { ReportingKPIData, ReportingType } from 'modules/reporting/types';
import type { ReportingDeliverableTypes } from 'modules/reporting/types/ReportingMetaInfo.type';

type ReportingKPIProgress = {
  instagram: ReportingKPIData | undefined | null,
  tiktok: ReportingKPIData | undefined | null
};

type OwnProps = {
  totalCreators: number,
  creatorsProgress: ReportingKPIProgress,
  followersProgress: ReportingKPIProgress,
  isLoading: boolean,
  campaignDeliverableTypes: Array<ReportingDeliverableTypes>,
  availableTypes: Array<ReportingType>
};

type Props = OwnProps;

const getBarChartData = (
  creatorsProgress: ReportingKPIProgress,
  followersProgress: ReportingKPIProgress,
  availableTypes: Array<ReportingType>,
  campaignDeliverableTypes: Array<ReportingDeliverableTypes>
): Array<BarChartProgressItem> => {
  const hasIGPosts = campaignDeliverableTypes.includes(DELIVERABLE_TYPE_INSTAGRAM_POST);
  const hasIGStories = campaignDeliverableTypes.includes(DELIVERABLE_TYPE_INSTAGRAM_STORY);
  const hasIGReels = campaignDeliverableTypes.includes(DELIVERABLE_TYPE_INSTAGRAM_REEL);
  const hasIGPostsandStories = hasIGPosts && hasIGStories;

  return availableTypes.reduce<Array<BarChartProgressItem>>((res, type) => {
    const creatorsProgressByChannel = creatorsProgress[type];
    const followersProgressByChannel = followersProgress[type];

    const igColors: Array<string> = [];
    if (type === 'instagram' && hasIGPosts) {
      igColors.push(COLORS.instagram_post.main);
    }

    if (type === 'instagram' && hasIGPostsandStories) {
      igColors.push(COLORS.instagram_story.main);
    }

    if (type === 'instagram' && hasIGReels) {
      igColors.push(COLORS.instagram_reel.main);
    }

    let color = COLORS.tiktok_video.main;
    if (type === 'instagram') {
      color = igColors[0];
    }

    if (creatorsProgressByChannel && followersProgressByChannel) {
      res.push({
        key: type,
        label: __t(`creator_follower_progress_widget.channels.${type}`),
        info: `${creatorsProgressByChannel.actual}/${creatorsProgressByChannel.agreed} creators`,
        actual: {
          percentage: Math.round(followersProgressByChannel.actual * 100 / followersProgressByChannel.agreed),
          metric: getNumFollowers(followersProgressByChannel.actual)
        },
        estimated: {
          percentage: 100,
          metric: getNumFollowers(followersProgressByChannel.agreed)
        },
        bgColor: color,
        complementaryColors: igColors.slice(1)
      });
    }

    return res;
  }, []);
};

class CreatorFollowerProgressWidget extends React.Component<Props> {
  render() {
    const { availableTypes, campaignDeliverableTypes, creatorsProgress, followersProgress, totalCreators, isLoading } = this.props;
    const barChartItems = getBarChartData(creatorsProgress, followersProgress, availableTypes, campaignDeliverableTypes);

    const classNames = classnames({
      'creator-follower-progress-widget': true,
      'creator-follower-progress-widget--centered': !isLoading && campaignDeliverableTypes.length === 2 && availableTypes.length !== 2
    });

    return (
      <div className={classNames}>
        <BarChartProgress
          data={barChartItems}
          title={__t('creator_follower_progress_widget.title')}
          emptyMessage={__t('creator_follower_progress_widget.no_content')}
          isLoading={isLoading}
          barStyle={{
            label: { marginTop: 10 },
            title: { marginBottom: 16 }
          }}
          tooltip={{
            title: __t('creator_follower_progress_widget.tooltip.title', { creators: totalCreators }),
            text: availableTypes.map(type => (
              __t(`creator_follower_progress_widget.tooltip.${type}_text`, {
                creators: creatorsProgress[type]?.agreed,
                followers: getNumFollowers(followersProgress[type]?.agreed)
              })
            )).join('<br/>')
          }}
          metricContainerType={getMetricTypeWidth(barChartItems)}
        />
      </div>
    );
  }
}

export default CreatorFollowerProgressWidget;
